// **React Imports
import { memo, useCallback, useContext, useEffect, useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

// **MUI Imports
import {
  AppBar,
  Box,
  Toolbar as MuiToolbar,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material'

// **Context Imports
import { SettingsContext } from '../../context/setting'

// **Custom Components Imports
import Icon from '../icon'
import AvailableCreditsBadge from './available-credits-badge'
import AppModeSwitch from './candidate-app-mode-switch'
import UserDropdown from './user-dropdown'

// **Hooks Imports

// **Utils Imports
import {
  APP_MODE,
  CANDIDATE_APP_MODE,
  CANDIDATE_APP_MODE_COOKIE_NAME,
  ROLES
} from '../../utils/constant'
import {
  checkCandidateAccess,
  checkDeveloperAccess,
  checkUserAccess,
  getCookie,
  hexToRGBA,
  showErrorToast
} from '../../utils/utils'

// **API Imports
import { validateToken } from '../../axios/api/auth'
import { getCandidateProfile } from '../../axios/api/candidate'
import { getPublishPermission } from '../../axios/api/challenge'

import { getClientIp, tokenActive } from '../../axios/api/general'
import { getProfileDetails, getUserTourGuide } from '../../axios/api/profile'
import { getListOfWorkspace } from '../../axios/api/workspace'

// **Image Imports
import LogoDark from '../../assets/images/logo-dark.svg'

// **Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import {
  resetIPLocation,
  setIPLocation,
  setProfileDetails,

  setUserRole,
  setUserTourProgress
} from '../../store/slice/auth'
import {
  setPublishPermission,
  setWorkspaces
} from '../../store/slice/workspace'

// **Third party imports
import { default as LogoWithWordmarkDark } from '../../assets/images/logo-with-wordmark-dark.svg'

// **SCSS Imports
import useDemoUser from '../../hooks/useDemoUser'
import './index.scss'
import DemoBanner from '../demo-banner'

// **Styled Component
const Toolbar = styled(MuiToolbar)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('md')]: {
    padding: '8px 16px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '4px 8px'
  }
}))

// **Separate Component
const ImageContainer = memo(
  ({ isMobileScreen, isGlobalCandidate = false, isPrepMode = false }) => {
    // hooks
    const { contestUri } = useParams()
    const {
      settings: { sidePanel, tenantConfig }
    } = useContext(SettingsContext)

    // variables
    const candidateMode = useMemo(
      () => getCookie(CANDIDATE_APP_MODE_COOKIE_NAME),
      []
    )

    const showHeaderLogo = useMemo(() => {
      if (isPrepMode) {
        return isMobileScreen
      }

      return (
        sidePanel?.hidden || contestUri || isGlobalCandidate || isMobileScreen
      )
    }, [
      sidePanel?.hidden,
      contestUri,
      isMobileScreen,
      isGlobalCandidate,
      isPrepMode
    ])

    const logoSrc = useMemo(() => {
      return tenantConfig?.logo || LogoDark
    }, [tenantConfig?.logo])

    const wordMarksSrc = useMemo(() => {
      return tenantConfig?.wordmark || LogoWithWordmarkDark
    }, [tenantConfig?.wordmark])

    return (
      showHeaderLogo && (
        <Box
          className="image-container"
          component={Link}
          to={
            contestUri && candidateMode !== CANDIDATE_APP_MODE.PRACTICE
              ? `/c/${contestUri}/stages`
              : isGlobalCandidate
                ? '/c'
                : '/'
          }
        >
          <img
            className="image-container-logo"
            src={isMobileScreen ? logoSrc : wordMarksSrc}
            alt="logo"
          />
        </Box>
      )
    )
  }
)

const AppBarContent = memo(
  ({
    showSubscriptionBadge = false,
    addBackgroundColor = false,
    isCandidate = false,
    isPrepMode = false
  }) => {
    // ** hooks
    const isSmallScreen = useMediaQuery('(max-width:450px)')

    return (
      <Box className="appbar-content-container">
        {showSubscriptionBadge && !isSmallScreen && (
          <AvailableCreditsBadge useCandidateMode={isPrepMode} />
        )}
        {isCandidate && (
          <AppModeSwitch addBackgroundColor={addBackgroundColor} />
        )}
        <UserDropdown
          addBackgroundColor={addBackgroundColor}
          isPrepMode={isPrepMode}
        />
      </Box>
    )
  }
)

const Header = ({ candidatePathHeader = false }) => {
  //Hooks
  const theme = useTheme()
  const location = useLocation()
  const dispatch = useDispatch()
  const { contestUri } = useParams()
  const { settings, setSettings } = useContext(SettingsContext)
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('lg'))
  const { isDemoUser } = useDemoUser()

  // Redux State
  const selectedWorkspace = useSelector(
    (state) => state.workspace.selectedWorkspace
  )

  const userDetails = useSelector((state) => state.auth.userData)
  const appMode = useSelector((state) => state.auth.appMode)
  const candidateAppMode = useSelector(
    (state) => state.candidate.candidateAppMode
  )

  //Variables
  const { skin, appBar, appBarBlur, appBarHidden, navHidden, mode, sidePanel } =
    settings

  const globalCandidatePath = useMemo(() => {
    const path = location.pathname?.toLowerCase()
    return path.endsWith('/c') || path.endsWith('/c/') || path.includes('/c/')
  }, [location.pathname])

  const isCandidate = useMemo(() => checkCandidateAccess(), [])

  const isDeveloper = useMemo(() => checkDeveloperAccess(), [])

  const showSubscriptionBadge = useMemo(
    () =>
      checkUserAccess([ROLES.ADMIN, ROLES.MODERATOR, ROLES.CONTRIBUTOR]) &&
      appMode === APP_MODE.HIRING,
    [appMode]
  )

  const isPrepMode = useMemo(() => {
    return (
      checkCandidateAccess() && candidateAppMode === CANDIDATE_APP_MODE.PRACTICE
    )
  }, [candidateAppMode])

  const addBackgroundColor = useMemo(() => {
    if (!isMobileScreen) return false

    if (isPrepMode) {
      return !sidePanel?.hidden
    }

    return (
      !sidePanel?.hidden &&
      !contestUri &&
      !globalCandidatePath &&
      !checkCandidateAccess()
    )
  }, [
    isMobileScreen,
    isPrepMode,
    sidePanel?.hidden,
    contestUri,
    globalCandidatePath
  ])

  //handlers
  const handleSidePanelToggling = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      sidePanel: {
        ...prev.sidePanel,
        opened: !prev.sidePanel.opened,
        expanded: !prev.sidePanel.opened
      }
    }))
  }, [setSettings])

  const tokenValidation = useCallback(async () => {
    if (contestUri) {
      await validateToken(contestUri, globalCandidatePath) // invited candidate
    } else if (globalCandidatePath) {
      await validateToken(null, globalCandidatePath) // global candidate
    } else {
      await validateToken() // user
    }
  }, [contestUri, globalCandidatePath])

  const setTokenHeartbeat = useCallback(() => {
    tokenActive()
  }, [])

  const fetchAllWorkspace = useCallback(async () => {
    const response = await getListOfWorkspace()
    if (response?.isError) {
      showErrorToast(response)
      dispatch(setWorkspaces({ workspaces: [] }))
    } else {
      dispatch(setWorkspaces({ workspaces: response }))
    }
  }, [dispatch])

  const fetchProfile = useCallback(
    async (isCandidate) => {
      const response = await (isCandidate
        ? getCandidateProfile()
        : getProfileDetails())

      if (response?.isError) {
        showErrorToast(response)
        dispatch(setProfileDetails({}))
      } else {
        dispatch(setProfileDetails(response))
      }
    },
    [dispatch]
  )

  const fetchPublishPermission = useCallback(
    async (workspaceId) => {
      const response = await getPublishPermission(workspaceId)
      if (response?.isError) {
        showErrorToast(response)
        dispatch(setPublishPermission(false))
      } else {
        dispatch(setPublishPermission(response))
      }
    },
    [dispatch]
  )

  const fetchUserTourGuide = useCallback(async () => {
    const response = await getUserTourGuide()
    if (response?.isError) {
      dispatch(setUserTourProgress([]))
    } else {
      dispatch(setUserTourProgress(response))
    }
  }, [dispatch])



  const fetchClientIP = useCallback(async () => {
    const response = await getClientIp()
    if (response?.isError) {
      showErrorToast(response)
      dispatch(resetIPLocation())
    } else {
      dispatch(setIPLocation(response))
    }
  }, [dispatch])

  //UseEffect
  useEffect(() => {
    if (selectedWorkspace?.id && !candidatePathHeader) {
      fetchPublishPermission(selectedWorkspace?.id)
    }
  }, [selectedWorkspace?.id, fetchPublishPermission, candidatePathHeader])

  useEffect(() => {
    if (!isCandidate && !isDeveloper) {
      fetchAllWorkspace()
      fetchClientIP()
    }
    if (!isDeveloper) {
      fetchProfile(isCandidate)
    }
  }, [fetchAllWorkspace, fetchProfile, isCandidate, isDeveloper, fetchClientIP])

  //useEffects
  useEffect(() => {
    if (!isDeveloper) {
      tokenValidation()
    }

    return () => {}
  }, [tokenValidation, isDeveloper])

  useEffect(() => {
    if (showSubscriptionBadge) {
      fetchUserTourGuide()
    }
  }, [fetchUserTourGuide, showSubscriptionBadge])

  useEffect(() => {
    let interval
    if (!isDeveloper) {
      setTokenHeartbeat()

      // Call Token Heartbeat every 60sec
      interval = setInterval(() => {
        setTokenHeartbeat()
      }, 60000)
    }
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [setTokenHeartbeat, isDeveloper])

  useEffect(() => {
    if (userDetails?.isSuperAdmin) {
      dispatch(setUserRole(ROLES.ADMIN?.toUpperCase()))
    } else if (userDetails?.isCandidate) {
      dispatch(setUserRole(ROLES.CANDIDATE?.toUpperCase()))
    } else if (userDetails?.isDeveloper) {
      dispatch(setUserRole(ROLES.DEVELOPER?.toUpperCase()))
    } else if (
      selectedWorkspace?.id &&
      Object.keys(userDetails?.workspaces || {}).length > 0
    ) {
      dispatch(
        setUserRole(userDetails?.workspaces?.[selectedWorkspace?.id] || null)
      )
    } else {
      dispatch(setUserRole(null))
    }
  }, [dispatch, selectedWorkspace?.id, userDetails])

  return (
    !appBarHidden && (
      <AppBar
        color="default"
        className={`header ${appBar === 'fixed' ? 'sticky' : 'static'}`}
        elevation={skin === 'bordered' ? 0 : 2}
        sx={{
          ...(appBar === 'static' && { zIndex: 13 }),
          transition: 'border-bottom 0.2s ease-in-out',
          ...(appBarBlur && { backdropFilter: 'blur(6px)' }),
          backgroundColor: (theme) =>
            hexToRGBA(
              contestUri || (isCandidate && !isPrepMode) || addBackgroundColor
                ? theme.palette.primary.main
                : theme.palette.background.default,
              appBarBlur ? 0.95 : 1
            ),
          ...(skin === 'bordered' && {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`
          }),
          boxShadow: 'none'
        }}
      >
        <Box
          className="header-navbar"
          id="navbar"
          sx={{
            width: '100%',
            // ...(addBackgroundColor
            //   ? { background: 'var(--primary-main' }
            //   : { background: 'var(--ba' }),
            ...(navHidden
              ? {}
              : {
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                })
          }}
        >
          <Toolbar
            className={`header-content-container full-width`}
            sx={{
              minHeight: (theme) =>
                `${theme.mixins.toolbar.minHeight - 1}px !important`
            }}
          >
            <>
              <Box className="header-flex-container">
                {!sidePanel?.hidden &&
                  isMobileScreen &&
                  !contestUri &&
                  (!globalCandidatePath || isPrepMode) && (
                    <Icon
                      icon={'ic:round-menu'}
                      className="menu-toggle-icon"
                      onClick={handleSidePanelToggling}
                      style={{
                        color: addBackgroundColor
                          ? 'var(--primary-contrastText)'
                          : ''
                      }}
                    />
                  )}

                <ImageContainer
                  mode={mode}
                  isMobileScreen={isMobileScreen}
                  isGlobalCandidate={globalCandidatePath}
                  isPrepMode={isPrepMode}
                />
              </Box>

              {isDemoUser && <DemoBanner />}

              <AppBarContent
                showSubscriptionBadge={showSubscriptionBadge || isPrepMode}
                addBackgroundColor={addBackgroundColor}
                isCandidate={isCandidate}
                isPrepMode={isPrepMode}
              />
            </>
          </Toolbar>
        </Box>
      </AppBar>
    )
  )
}

export default memo(Header)
